/** Which sizes exist, in what order, and how a POM row's gradings line up with them (BM-9210). */

import type { MeasurementProduct, MeasurementsState, SpecGrading, SpecPomItem } from './types';

/** A size-range entry is a plain name, but legacy documents store `{ name }` — reduce either to the name. */
function toSizeName(entry: unknown): string {
  if (typeof entry === 'string') return entry;
  const name = (entry as { name?: unknown } | null)?.name;
  return typeof name === 'string' ? name : '';
}

/**
 * Size names this product actually grades: its own saved `sizeRanges` (the sizes selected for the
 * product), which the API already falls back to the grading group's range when the product has none.
 * A size outside this list is still calculated and still shows its value — it is only locked against edits.
 */
export function getProductSizeNames(product: MeasurementProduct): string[] {
  return (product.sizeRanges ?? []).map(toSizeName).filter(Boolean);
}

/**
 * The grid's size columns: the grading group's range first, then any size selected on a product but
 * absent from that range, in first-seen order. Products under one group can carry different size
 * subsets, and every size in this union is calculated for every product.
 */
export function unionSizeNames(groupSizeRanges: string[], products: MeasurementProduct[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  const add = (name: string): void => {
    if (name && !seen.has(name)) {
      seen.add(name);
      result.push(name);
    }
  };
  /* The group range is already normalised to plain names by the API (`normalizeSizeRanges`). */
  (groupSizeRanges ?? []).forEach(add);
  products.forEach((product) => getProductSizeNames(product).forEach(add));
  return result;
}

/**
 * Every size the calculation runs over, for the current module state. Hiding never narrows this list:
 * a hidden size keeps its gradings and every size after it still grades through it (BM-9877), and the
 * calc engines walk `gradings` positionally, so dropping one here would re-derive its neighbours from
 * the wrong size. Filter the COLUMNS with `getVisibleSizeNames`, never this.
 */
export function getParentSizeRanges(state: MeasurementsState): string[] {
  return unionSizeNames(state.sizeRanges ?? [], state.products);
}

/** Sizes hidden on one product's specification; an absent list means nothing is hidden. */
export function getProductHiddenSizeNames(product: MeasurementProduct): string[] {
  return (product.hiddenSizeNames ?? []).filter(Boolean);
}

/**
 * Whether this product hides this size. Hiding is one of the two reasons a size renders blank; the
 * other is the size lying outside the product's own range. They stay distinct because only hiding is
 * a deliberate user act — ask `isSizeRenderedForProduct` for the combined answer.
 */
export function isSizeHiddenForProduct(product: MeasurementProduct, sizeName: string): boolean {
  return getProductHiddenSizeNames(product).includes(sizeName);
}

/**
 * Whether one product shows this size at all, given its own size range and hidden list. Two states
 * render blank: a size the product HIDES, which is display-only, and a size outside the product's
 * own `sizeRanges` — one it simply does not have. The grid draws a single union column set across
 * the cohort, so blanking the cell is the only thing that can tell two products under one grading
 * group apart. A product carrying no range of its own states no opinion, so every size stands.
 * Blanking never touches `gradings`: the size keeps calculating and the chain still walks it.
 */
export function isSizeRendered(sizeRanges: string[], hiddenSizeNames: string[], sizeName: string): boolean {
  if (hiddenSizeNames.includes(sizeName)) return false;
  return sizeRanges.length === 0 || sizeRanges.includes(sizeName);
}

/** `isSizeRendered` for a loaded product, reading both lists off the product itself. */
export function isSizeRenderedForProduct(product: MeasurementProduct, sizeName: string): boolean {
  return isSizeRendered(getProductSizeNames(product), getProductHiddenSizeNames(product), sizeName);
}

/**
 * The size columns to render: every calculated size at least ONE loaded product still shows. A size
 * shown by some products keeps its column — the rest render a blank cell — because one product's hide
 * or narrower size range must not remove a size another product still shows. A size no loaded product
 * shows would be an all-blank column, so it is dropped. The set is derived from the products loaded
 * so far, so a Load-More page that shows the size brings its column back. With no products loaded
 * nothing has stated an opinion yet, so every size stands.
 */
export function getVisibleSizeNames(state: MeasurementsState): string[] {
  const sizeNames = getParentSizeRanges(state);
  const { products } = state;
  if (products.length === 0) return sizeNames;
  return sizeNames.filter((sizeName) => products.some((product) => isSizeRenderedForProduct(product, sizeName)));
}

/**
 * Give a POM row one grading entry per size column, in column order. A size the product never graded
 * gets a zero-delta entry rather than being skipped, so the calculation engines — which walk `gradings`
 * positionally from the anchor outward — produce and store a measurement for it like any other size.
 * The delta is an explicit `'0'` so the cell reads as a real zero instead of a blank.
 * Sizes stored on the row but absent from the column set are kept (never silently dropped). Pure.
 */
export function alignGradingsToSizes(item: SpecPomItem, sizeNames: string[]): SpecPomItem {
  if (sizeNames.length === 0) return item;
  const gradings = item.gradings ?? [];
  const bySizeName = new Map<string, SpecGrading>();
  gradings.forEach((grading) => {
    if (!bySizeName.has(grading.sizeName)) bySizeName.set(grading.sizeName, grading);
  });

  const columnSizes = new Set(sizeNames);
  const aligned = sizeNames.map<SpecGrading>((sizeName) => bySizeName.get(sizeName) ?? { sizeName, measurment: '', grading: '0' });
  const extras = gradings.filter((grading) => !columnSizes.has(grading.sizeName));
  return { ...item, gradings: [...aligned, ...extras] };
}
